import React, { useEffect, useRef } from 'react';

import { HeartbeatLife, createGrid } from './gameOfLifeModel.js';

const CELL_SIZE = 10;
const ECG_COLOR = [121, 239, 150];

const drawGrid = (context, grid, width, height, pulse) => {
  context.clearRect(0, 0, width, height);
  const color = ECG_COLOR.map((channel) => Math.round(255 + (channel - 255) * pulse));
  context.fillStyle = `rgba(${color.join(',')},${0.07 + 0.15 * pulse})`;
  grid.forEach((column, x) =>
    column.forEach((alive, y) => {
      if (alive) context.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE - 1, CELL_SIZE - 1);
    }),
  );
};

const GameOfLifeCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return undefined;

    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let width = window.innerWidth;
    let height = window.innerHeight;
    let animationFrame;
    const life = new HeartbeatLife({
      grid: createGrid({ cols: Math.ceil(width / CELL_SIZE), rows: Math.ceil(height / CELL_SIZE) }),
      seconds: performance.now() / 1000,
    });

    const draw = (timestamp) => {
      if (motionPreference.matches || document.hidden) {
        drawGrid(context, life.grid, width, height, 0);
        return;
      }
      const pulse = life.update(timestamp / 1000);
      drawGrid(context, life.grid, width, height, pulse);
      animationFrame = window.requestAnimationFrame(draw);
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      life.resize(Math.ceil(width / CELL_SIZE), Math.ceil(height / CELL_SIZE));
      drawGrid(context, life.grid, width, height, 0);
    };

    const syncAnimation = () => {
      window.cancelAnimationFrame(animationFrame);
      life.resync(performance.now() / 1000);
      if (motionPreference.matches || document.hidden) {
        drawGrid(context, life.grid, width, height, 0);
        return;
      }
      animationFrame = window.requestAnimationFrame(draw);
    };

    resize();
    syncAnimation();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', syncAnimation);
    motionPreference.addEventListener('change', syncAnimation);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', syncAnimation);
      motionPreference.removeEventListener('change', syncAnimation);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: -1,
      }}
    />
  );
};

export default GameOfLifeCanvas;
